import React, { useEffect, useState } from "react";
import { fmtDate } from "@/lib/dates";
import { api, apiErr } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Check, CreditCard, Star, BedDouble, Clock } from "lucide-react";
import { openAskJane } from "@/components/AdvisorWidget";
import { FALLBACK_BANDS } from "@/pages/Pricing";
import { EmailJaneLink } from "@/components/EmailJaneLink";

const bedsSaveLabel = (busy, beds) => { if (busy) return "Saving…"; return beds ? "Update" : "Save"; };
const statusLine = (home, active, planLabel) => {
  if (!home) return "…";
  if (!active) return home.subscription_status;
  return `Active — ${planLabel}${home.band ? ` · ${home.band.label}` : ""}`;
};
const bandLabel = (tooLarge, band) => { if (tooLarge) return "151+ beds · Bespoke"; return band ? band.label : "Tell us your registered beds"; };

const gbp = (n) => `£${Number(n).toLocaleString("en-GB", { minimumFractionDigits: Number.isInteger(Number(n)) ? 0 : 2, maximumFractionDigits: 2 })}`;
const FEATURES = ["Unlimited staff logins", "Full Essential Oral Care training", "CQC evidence export", "Oral Champion booking"];

function BedsForm({ home, onSaved }) {
  const [beds, setBeds] = useState(home.beds || "");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.put("/care-home/me", { beds: Number(beds) }); onSaved(data); toast.success("Registered beds updated"); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="flex flex-wrap items-end gap-3" data-testid="beds-form">
      <div>
        <Label className="text-xs">Registered beds</Label>
        <Input data-testid="beds-input" type="number" min={1} max={2000} required value={beds} onChange={(e) => setBeds(e.target.value)} className="w-32" />
      </div>
      <Button data-testid="beds-save-btn" type="submit" disabled={busy} variant="outline" className="rounded-full">{bedsSaveLabel(busy, home.beds)}</Button>
    </form>
  );
}

export default function Billing() {
  const [bands, setBands] = useState(FALLBACK_BANDS);
  const [home, setHome] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/billing/bands").then((r) => { if (r.data?.bands?.length) setBands(r.data); }).catch(() => {});
    api.get("/care-home/me").then((r) => setHome(r.data)).catch(() => {});
  }, []);

  const checkout = async (lk) => {
    setBusy(lk);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: lk, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); setBusy(""); }
  };

  const active = home?.subscription_status === "active";
  const band = bands && home?.band ? bands.bands.find((b) => b.key === home.band.key) : null;
  const tooLarge = home?.beds > 150;
  const planLabel = "Annual";

  return (
    <div>
      <PageHeader title="Billing & subscription" subtitle="One Care Home Licence covers your whole team. Priced by registered beds, not by staff." />
      <div className={`rounded-2xl p-6 mb-8 border ${active ? "bg-rag-green-bg border-rag-green/20" : "bg-coral/10 border-coral/20"}`} data-testid="subscription-status">
        <div className="label-caps text-muted-foreground">Current status</div>
        <div className="text-2xl font-bold text-brand mt-1 capitalize">
          {statusLine(home, active, planLabel)}
        </div>
        {home && !active && home.trial_ends_at && <p className="text-sm text-muted-foreground mt-1">Trial expires {fmtDate(home.trial_ends_at)}. Subscribe to keep your compliance evidence.</p>}
        {home && active && home.renewal_date && <p className="text-sm text-muted-foreground mt-1">Renews {fmtDate(home.renewal_date)}.</p>}
      </div>

      {home && !active && (
        <>
          <div className="rounded-2xl border border-cream-deep bg-cream-card p-6 mb-6 flex flex-wrap items-center justify-between gap-4" data-testid="band-card">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-brand-light grid place-items-center"><BedDouble className="h-5 w-5 text-brand" /></div>
              <div>
                <div className="label-caps text-muted-foreground">Your licence band</div>
                <div className="font-bold text-brand" data-testid="band-label">{bandLabel(tooLarge, band)}</div>
              </div>
            </div>
            <BedsForm home={home} onSaved={setHome} />
          </div>

          {tooLarge && (
            <div data-testid="bespoke-card" className="rounded-2xl border border-brand/20 bg-brand-light/40 p-6 max-w-3xl">
              <p className="font-semibold text-brand">Homes with 151+ beds are priced individually.</p>
              <p className="text-sm text-muted-foreground mt-1">Ask Jane for a bespoke quote and we'll set your licence up for you.</p>
              <Button data-testid="bespoke-contact-btn" onClick={() => openAskJane(`Hi Jane, our care home has ${home.beds} registered beds and we'd like a bespoke Care Home Licence quote.`)} className="mt-4 rounded-full bg-brand text-white">Contact Jane</Button>
            </div>
          )}

          {band && !tooLarge && (
            <div className="grid md:grid-cols-[1fr_1fr] gap-6 max-w-3xl">
              <div data-testid={`billing-plan-${band.annual_lookup}`} className="rounded-2xl border p-7 bg-brand text-white border-brand">
                <Badge className="bg-coral text-white hover:bg-coral mb-3"><Star className="h-3 w-3 mr-1" />{bands.intro_active ? "Introductory offer" : "Best value"}</Badge>
                <h3 className="text-lg font-bold">Annual Care Home Licence</h3>
                <div className="mt-3">
                  <span className="text-3xl font-bold font-heading" data-testid="annual-price">{gbp(bands.intro_active ? band.intro : band.renewal)}</span>
                  <span className="text-white/70"> {bands.intro_active ? "first year" : "/ year"}</span>
                  {bands.intro_active && <div className="text-xs text-white/60 mt-1">then {gbp(band.renewal)}/year on renewal</div>}
                  {bands.intro_locked_until && <div data-testid="intro-locked" className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white"><Clock className="h-3.5 w-3.5" />Your introductory price is locked in until {fmtDate(bands.intro_locked_until)}</div>}
                </div>
                <ul className="mt-4 space-y-2 text-sm">{FEATURES.map((f) => <li key={f} className="flex items-center gap-2"><Check className="h-4 w-4 text-coral" /><span className="text-white/90">{f}</span></li>)}</ul>
                <Button data-testid={`subscribe-${band.annual_lookup}`} onClick={() => checkout(band.annual_lookup)} disabled={!!busy} className="mt-6 w-full rounded-full bg-coral hover:bg-coral-hover text-white">
                  <CreditCard className="h-4 w-4 mr-2" />{busy === band.annual_lookup ? "Redirecting…" : "Subscribe annually"}
                </Button>
              </div>
              <div data-testid="why-annual" className="rounded-2xl border border-cream-deep bg-cream-card p-7 text-sm text-muted-foreground space-y-3">
                <h3 className="text-lg font-bold text-brand">Why one annual licence?</h3>
                <p>CQC expects oral-health training, audits and evidence to be kept up across the whole year — so your licence runs for 12 months, with reminders, re-audits and new-starter access included.</p>
                <p>Your Oral Champion place is part of the licence, not a one-off purchase.</p>
                <p>Need to spread the cost? <EmailJaneLink testId="billing-email-jane" className="underline text-brand hover:text-coral">Email Jane</EmailJaneLink> about paying in two instalments.</p>
              </div>
            </div>
          )}
        </>
      )}
      {active && <p className="text-muted-foreground">Your subscription is active. Thank you for being a KOHC member!</p>}
    </div>
  );
}
